import { Component, OnChanges, OnDestroy, input, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog'
import { Title } from '@angular/platform-browser';
import { faEdit, faTrash, faCommentSlash, faMessage, faFileArrowDown, faFilePen, faCheck } from '@fortawesome/free-solid-svg-icons';
import { ReportService } from 'src/app/services/report.service';
import { NewCommentComponent } from './new-comment/new-comment.component';
import { UserService } from 'src/app/services/user.service';
import { DeleteCommentComponent } from './delete-comment/delete-comment.component';
import { ConfirmationToastComponent } from 'src/app/components/confirmation-toast/confirmation-toast.component';
import { PrintReport25008Component } from './view-sections/print-report/print-report-2500-8.component';
import { DialogService } from 'src/app/services/dialog.service';
import { ViewportScroller } from '@angular/common';
import { Subject } from 'rxjs';

@Component({
    selector: 'app-report-2500-8',
    templateUrl: './report-2500-8.component.html',
    styleUrls: ['./report-2500-8.component.scss', './report-comments.scss'],
    standalone: false
})


export class Report25008Component implements OnChanges, OnDestroy {
  //icons
  faEdit = faEdit;
  faTrash = faTrash;
  faCommentSlash = faCommentSlash;
  faFileArrowDown = faFileArrowDown;
  faMessage = faMessage;
  faFile = faFilePen;
  faCheck = faCheck;

  report_id = input.required<number>();
  report_page_input = input<string>();
  report_page: number = 0;

  report: any;
  report_status: any;
  complete: boolean = false;
  nav = signal<string>('');
  current_status: number | undefined = undefined;
  autosave: any = { saving: false, saved: false, saveTime: new Date(), updateTimer: new Subject() };
  comment_expand: boolean = false;
  comments: any;
  comments_unresolved: boolean = false;
  unresolved: boolean = false;
  reportSections: any;

  currentUser: any;
  is_co: boolean = false;

  saveTimerID: any = null;
  savedText = "Last Saved a Few Seconds Ago"

  pageNames = [
    "Report Type",
    "Burned Area Description",
    "Watershed Condition",
    "Summary of Analysis",
    "Recommended Treatment Narratives",
    "Emergency Stabilization Treatments & Requested Funds",
    "Team & Supporting Documents",
    "Select Forest Unit Line Officer(s)"
  ]

  maxPage = 8

  report_type = "funding"

  constructor(
    public dialog: MatDialog,
    private readonly titleService: Title,
    private readonly reportService: ReportService,
    private readonly userService: UserService,
    private readonly toast: ConfirmationToastComponent,
    private readonly dialogService: DialogService,
    private readonly viewportScroller: ViewportScroller,
  ) {
    this.titleService.setTitle("BAER - Edit Report");
    this.reportSections = this.reportService.getReportPageList();
    this.saveTimerID = setInterval(() => {
      this.updateTimer()
    }, 10000);
    this.autosave.updateTimer.subscribe(() => {
      this.updateTimer()
    })
  }

  updateReportType(report: any): boolean {
    if (report.report_type != this.report_type) {
      this.report_type = report.report_type

      if (this.report_type == "No Treatment") {
        this.pageNames = [
          "Report Type",
          "Burned Area Description",
          "Watershed Condition",
          "Summary of Analysis",
          "Assessment Cost",
          "Team & Supporting Documents",
          "Select Forest Unit Line Officer(s)"
        ]
        this.maxPage = 7
      } else if (this.report_type == "Pre-assessment Request") {
        this.pageNames = [
          "Report Type",
          "Burned Area Description",
          "Summary of Analysis",
          "Recommended Treatment Narratives",
          "Emergency Stabilization Treatments & Requested Funds",
          "Team & Supporting Documents",
          "Select Forest Unit Line Officer(s)"
        ]
        this.maxPage = 7
      } else {
        this.pageNames = [
          "Report Type",
          "Burned Area Description",
          "Watershed Condition",
          "Summary of Analysis",
          "Recommended Treatment Narratives",
          "Emergency Stabilization Treatments & Requested Funds",
          "Team & Supporting Documents",
          "Select Forest Unit Line Officer(s)"
        ]
        this.maxPage = 8
      }
      this.ngOnChanges()
      return true
    }
    return false
  }

  ngOnChanges(): void {
  this.viewportScroller.scrollToPosition([0,0])
    this.dialogService.openSpinner();
    let relations: string[] = []
    let page = (Number.parseInt(this.report_page_input() ?? ""))
    page ??= 1
    let pageName = this.pageNames[page - 1]
    if (page >= this.pageNames.length) pageName = "End"
    switch (pageName) {
      case "Report Type":
        relations = ["assessment"]
        break
      case "Burned Area Description":
        relations = ["assessment", "stateCounties", "supporting_files", "watersheds", "forests.ranger_districts", "soil_burn_severities"]
        break
      case "Watershed Condition":
        relations = ["assessment", "stateCounties", "supporting_files", "watersheds", "soil_burn_severities"]
        break
      case "Summary of Analysis":
        relations = ["assessment", "supporting_files"]
        break
      case "Recommended Treatment Narratives":
        relations = ["assessment", "forests", "forests.treatments", "forests.treatments.treatment_specifications"]
        break
      case "Emergency Stabilization Treatments & Requested Funds":
        relations = ["assessment", "forests", "forests.treatments", "forests.treatments.treatment_specifications", "forests.treatments.treatment_option", "forests.treatments.treatment_type","users.skills", "users.user"]
        break
      case "Team & Supporting Documents":
        relations = ["assessment", "users", "users.user", "users.skills.skill", "supporting_files"]
        break
      case "Select Forest Unit Line Officer(s)":
        relations = ["assessment", "forests", "forests.line_officer"]
        break
      case "End":
        relations = ["assessment", "forests", "forests.line_officer", "forests.line_officer.baer_roles", "forests.line_officer.baer_roles.baer_role"]
        break
      default:
        relations = ["assessment"]
    }
    this.reportService.getReport(this.report_id(), relations).subscribe((report: any) => {
      if (this.updateReportType(report.reports[0])) return
      this.report = report.reports[0];
      this.titleService.setTitle(`BAER - Edit ${this.report.baer_name} (${this.report.report_num}) Report`);
      this.setReportComments();
      this.setReportStatus();
      this.setPage();
      this.nav.set('');
    });
    this.userService.getUserProfile().then((user) => {
      this.currentUser = user;
      if ((this.currentUser.isROCO?.length ?? 0) > 0) this.is_co = true;
    })
  }

  subLoaded() {
    this.dialogService.closeSpinner();
  }

  ngOnDestroy() {
    // end timeout when we leave the page
    if (this.saveTimerID) {
      clearInterval(this.saveTimerID);
    }
  }

  save_close() {
    this.nav.set('close');
  }

  forward() {
    this.nav.set('forward');
  }

  back() {
    this.nav.set('back');
  }

  goToPage(page: number) {
    if (this.report_page == page) return // don't navigate to current page
    this.nav.set(page.toString());
  }

  setReportComments() {
    this.reportService.getReportComments(this.report_id()).subscribe((reportComments: any) => {
      this.comments = reportComments.reportCommentEntities;
      let findUnresolved = this.comments.find((x: any) => !x.resolved)
      if (findUnresolved ?? false) this.comments_unresolved = true;
      else this.comments_unresolved = false;
      if (this.comments_unresolved || !this.complete) this.unresolved = true;
      else this.unresolved = false;
    })
  }

  statusChange(status: any) {
    this.current_status = status;
    this.setReportStatus();
    const filtered_status = this.report_status.filter((status: any) => status.status < 2)
    if (filtered_status.length >= 1) {
      this.complete = false;
    } else this.complete = true;
    if (this.comments_unresolved || !this.complete) this.unresolved = true;
    else this.unresolved = false;
  }

  setReportStatus() {
    let page8_status = this.report.page_8_status_id;
    if (this.report_page == 8 && this.current_status)
      page8_status = this.current_status;

    this.report_status = [
      { report_page: 1, status: this.report.page_1_status_id },
      { report_page: 2, status: this.report.page_2_status_id },
      { report_page: 3, status: this.report.page_3_status_id },
      { report_page: 4, status: this.report.page_4_status_id },
      { report_page: 5, status: this.report.page_5_status_id },
      { report_page: 6, status: this.report.page_6_status_id },
      { report_page: 7, status: this.report.page_7_status_id },
      { report_page: 8, status: page8_status }
    ]
    if (this.maxPage == 7) {
      let page7_status = this.report.page_7_status_id;
      if (this.report_page == 7 && this.current_status)
        page7_status = this.current_status;

      this.report_status = [
        { report_page: 1, status: this.report.page_1_status_id },
        { report_page: 2, status: this.report.page_2_status_id },
        { report_page: 3, status: this.report.page_3_status_id },
        { report_page: 4, status: this.report.page_4_status_id },
        { report_page: 5, status: this.report.page_5_status_id },
        { report_page: 6, status: this.report.page_6_status_id },
        { report_page: 7, status: page7_status }
      ]
    }
  }

  setPage() {
    //if no page passed in, set page to latest page not finished
    if (this.report_page_input()) {
      this.report_page = (Number.parseInt(this.report_page_input() ?? '') ?? 0);
    }
    else {
      const filtered_status = this.report_status.filter((status: any) => status.status < 2)
      if (filtered_status.length >= 1) {
        // const lowestItem = filtered_status?.reduce((prev: any, curr: any) => (prev.report_page < curr.report_page ? prev : curr));
        // this.report_page = lowestItem.report_page ?? 8;
        // issue with dynamic page load. report must load first to get status but then report details is dynamic after page select so there is circular logic. defaulting to page 1 for now
        this.report_page = 1;
        this.complete = false;
      } else {
        this.report_page = 1;
        this.complete = true;
      }
    }
    if (this.comments_unresolved || !this.complete) this.unresolved = true;
    else this.unresolved = false;
  }

  addComment() {
    this.dialog.open(NewCommentComponent, {
      width: "600px",
      data: {
        'report_num': this.report_page,
        report_id: this.report_id(),
        edit: false,
        user: this.currentUser
      },
      panelClass: ['animate__animated', 'animate__fadeInDown'],
    }).afterClosed().subscribe((reload) => {
      if (reload) {
        this.setReportComments();
      }
    })
  }

  editComment(comment: any) {
    this.dialog.open(NewCommentComponent, {
      width: "600px",
      data: {
        'report_num': comment.section,
        report_id: this.report_id(),
        comment: comment,
        edit: true,
        user: this.currentUser
      },
      panelClass: ['animate__animated', 'animate__fadeInDown'],
    }).afterClosed().subscribe((reload) => {
      if (reload) {
        this.setReportComments();
      }
    })
  }

  deleteComment(comment: any) {
    this.dialog.open(DeleteCommentComponent, {
      width: "440px",
      panelClass: ['animate__animated', 'animate__fadeInDown'],
      data: {
        comment_id: comment.report_comment_id
      }
    }).afterClosed().subscribe((reload) => {
      if (reload) {
        this.setReportComments();
      }
    })
  }

  markAsResolved(comment: any, resolved: boolean) {
    this.reportService.editReportComment({
      resolved: resolved
    }, comment.report_comment_id).subscribe({
      next: (response) => {
        comment.resolved = resolved
      },
      error: (response) => {
        this.toast.openErrorSnackBar(`There was an error marking commment ${resolved ? 'resolved' : 'unresolved'}`, "Please try again.");
      },
    });
  }

  findPage(pageNum: any) {
    return this.reportSections.find((x: any) => x.pageNum == pageNum).pageName;
  }

  updateTimer() {
    let minutes = Math.floor((Date.now() - this.autosave.saveTime.getTime()) / 60000)
    if (minutes < 1) {
      this.savedText = "Last Saved a Few Seconds Ago"
    } else if (minutes == 1) {
      this.savedText = "Last Saved 1 Minute Ago"
    } else if (minutes >= 60 * 24) {
      if (minutes < 60 * 24 * 2) {
        this.savedText = `Last Saved 1 Day Ago`
      } else {
        this.savedText = `Last Saved ${Math.floor(minutes / (60 * 24))} Days Ago`
      }
    } else if (minutes >= 60) {
      if (minutes < 60 * 2) {
        this.savedText = `Last Saved 1 Hour Ago`
      } else {
        this.savedText = `Last Saved ${Math.floor(minutes / (60))} Hours Ago`
      }
    } else {
      this.savedText = `Last Saved ${minutes} Minutes Ago`
    }
  }

  download(report_id: any) {
    this.dialog.open(PrintReport25008Component, {
      width: "1700px",
      height: "0px",
      data: {
        report_id: report_id
      }
    });
  }
}
